import React, { useEffect, useState } from 'react';
import { Accordion, Button, Card, Container, Table } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import DatePicker from 'react-datepicker';
import { fi } from 'date-fns/locale';
import * as signalR from '@microsoft/signalr';
import { Icon } from '@lomarengas/design-system';
import { format } from 'date-fns';
import {
  addStatusToast,
  InputCheck,
  RevertConfirmation,
  SectionHeader,
} from '../../common/components';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import signalRPricingService from '../../common/hubs/signalRPricingService';
import {
  clearSeasonRateBatchUpdateDetails,
  fetchLastTagOperation,
  fetchSeasonRateBatchUpdateDetails,
  type LastTagOperationDto,
  modifyDatesForSeasonRates,
} from './rateToolsSlice';
import LastTagOperationInfo from './LastTagOperationInfo';
import { formatDateToFinnish } from '../../common/utils/dateUtils';
import { TagSelector } from '../../common/components/TagSelector';
import { AccommodationSelector } from '../../common/components/AccommodationSelector';
import {
  SEASON_RATE_BATCH_OPERATION_TYPE,
  SEASON_RATE_BATCH_OPERATION_TYPE_DESCRIPTIONS,
} from '../../common/constants';

interface CopyMessage {
  accommodationNumber?: string;
  accommodationName?: string;
  seasonRatesChangedCount?: string;
}

interface UpdateSummary {
  accommodationsModifiedCount: string;
  accommodationsNotModifiedCount: string;
}

const ModifySeasonRatesTool = () => {
  const dispatch = useAppDispatch();
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [lastTagOperation, setLastTagOperation] =
    useState<LastTagOperationDto | null>(null);
  const [selectedAccommodationId, setSelectedAccommodationId] = useState<
    string | null
  >(null);
  const [selectedSeasonStartDate, setSelectedSeasonStartDate] =
    useState<Date | null>(null);
  const [selectedSeasonEndDate, setSelectedSeasonEndDate] =
    useState<Date | null>(null);
  const [selectedNewSeasonStartDate, setSelectedNewSeasonStartDate] =
    useState<Date | null>(null);
  const [selectedNewSeasonEndDate, setSelectedNewSeasonEndDate] =
    useState<Date | null>(null);
  const [deleteOverlappingSeasons, setDeleteOverlappingSeasons] =
    useState<boolean>(true);
  const [copyMessages, setCopyMessages] = useState<CopyMessage[]>([]);
  const [updateSummary, setUpdateSummary] = useState<UpdateSummary | null>(
    null,
  );
  const [isCopying, setIsCopying] = useState<boolean>(false);

  const seasonRateBatchUpdateDetails = useAppSelector(
    state => state.rateTools.seasonRateBatchUpdateDetails,
  );
  const [revertModalState, setRevertModalState] = useState<{
    show: boolean;
    batchId?: string;
    operationType?: SEASON_RATE_BATCH_OPERATION_TYPE;
  }>({ show: false });

  useEffect(() => {
    dispatch(clearSeasonRateBatchUpdateDetails());
    dispatch(
      fetchSeasonRateBatchUpdateDetails(
        SEASON_RATE_BATCH_OPERATION_TYPE.DateChange,
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    if (
      signalRPricingService.connection.state ===
      signalR.HubConnectionState.Disconnected
    ) {
      console.log('start connection');
      signalRPricingService.startConnection();
    }

    signalRPricingService.onReceiveMessage = (
      accommodationNumber: string,
      accommodationName: string,
      seasonRatesChangedCount: string,
    ) => {
      setCopyMessages(prevCopyMessages => [
        ...prevCopyMessages,
        { accommodationNumber, accommodationName, seasonRatesChangedCount },
      ]);
    };

    signalRPricingService.onReceiveUpdateSummary = (
      accommodationsModifiedCount: string,
      accommodationsNotModifiedCount: string,
    ) => {
      setUpdateSummary({
        accommodationsModifiedCount,
        accommodationsNotModifiedCount,
      });
    };

    signalRPricingService.onReceiveRevertSummary = (
      revertedCount: string,
      conflictCount: string,
    ) => {
      const conflictSuffix =
        Number(conflictCount) > 0
          ? ` (${conflictCount} konfliktia ohitettu)`
          : '';
      dispatch(
        addStatusToast({
          Message: `Palautus valmis: ${revertedCount} riviä palautettu${conflictSuffix}.`,
          IsFailed: false,
        }),
      );
      dispatch(
        fetchSeasonRateBatchUpdateDetails(
          SEASON_RATE_BATCH_OPERATION_TYPE.DateChange,
        ),
      );
    };
  }, []);

  const handleModifyDatesForSeasonRates = async () => {
    if (
      (selectedTagId || selectedAccommodationId) &&
      selectedSeasonStartDate &&
      selectedSeasonEndDate &&
      selectedNewSeasonStartDate &&
      selectedNewSeasonEndDate
    ) {
      setCopyMessages([]);
      setUpdateSummary(null);
      setIsCopying(true);
      await dispatch(
        modifyDatesForSeasonRates({
          accommodationId: selectedAccommodationId,
          tagId: selectedTagId,
          existingSeasonRateStartDate: formatDateToFinnish(
            selectedSeasonStartDate,
          ),
          existingSeasonRateEndDate: formatDateToFinnish(selectedSeasonEndDate),
          newSeasonRateStartDate: formatDateToFinnish(
            selectedNewSeasonStartDate,
          ),
          newSeasonRateEndDate: formatDateToFinnish(selectedNewSeasonEndDate),
          deleteOverlappingSeasons,
        }),
      );
      setIsCopying(false);
    }
  };

  const handleTagSelected = async (tagId: string) => {
    setSelectedTagId(tagId);
    setSelectedAccommodationId(null);

    if (tagId === null) {
      setLastTagOperation(null);
      setSelectedSeasonStartDate(null);
      setSelectedSeasonEndDate(null);
      setSelectedNewSeasonStartDate(null);
      setSelectedNewSeasonEndDate(null);
      setDeleteOverlappingSeasons(true);
    } else {
      const result = await dispatch(
        fetchLastTagOperation({
          tagId,
          operationType: SEASON_RATE_BATCH_OPERATION_TYPE.DateChange,
        }),
      );
      setLastTagOperation(
        (result.payload as LastTagOperationDto | null) ?? null,
      );
    }
  };

  const handleAccommodationSelected = (accommodationId: string) => {
    setSelectedAccommodationId(accommodationId);
    setSelectedTagId(null);

    if (accommodationId === null) {
      setSelectedSeasonStartDate(null);
      setSelectedSeasonEndDate(null);
      setSelectedNewSeasonStartDate(null);
      setSelectedNewSeasonEndDate(null);
      setDeleteOverlappingSeasons(true);
    }
  };

  return (
    <>
      <Helmet>
        <title>Hinnoittelu - Hintakausien joukkomuutokset</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Hinnoittelu']}
          Title="Hintakausien joukkomuutokset"
        />
        <div className="my-48">
          <div
            className="border bg-white rounded shadow-lg mx-auto p-24 p-xl-32"
            style={{ maxWidth: 640 }}
          >
            <TagSelector
              filterActiveAccommodations
              Subtitle="Valitse tagi, jonka kohteisiin muutokset liittyvät"
              Title="Hintakausien päivämäärien muutokset"
              onTagSelected={handleTagSelected}
            />
            <LastTagOperationInfo lastOperation={lastTagOperation} />
            {selectedTagId && (
              <>
                <p className="text-gray-600 mt-16 mb-16">
                  Hintakauden nykyinen alkamispäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedSeasonStartDate(date)}
                  className="form-control"
                  selected={selectedSeasonStartDate}
                  showWeekNumbers
                  showYearDropdown
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Hintakauden nykyinen päättymispäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedSeasonEndDate(date)}
                  className="form-control mb-16"
                  selected={selectedSeasonEndDate}
                  minDate={selectedSeasonStartDate ?? null}
                  disabled={!selectedSeasonStartDate}
                  showYearDropdown
                  showWeekNumbers
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Aseta hintakauden uusi alkamispäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedNewSeasonStartDate(date)}
                  className="form-control"
                  selected={selectedNewSeasonStartDate}
                  showWeekNumbers
                  showYearDropdown
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Aseta hintakauden uusi päättymispäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedNewSeasonEndDate(date)}
                  className="form-control mb-16"
                  selected={selectedNewSeasonEndDate}
                  minDate={selectedNewSeasonStartDate ?? null}
                  disabled={!selectedNewSeasonStartDate}
                  showYearDropdown
                  showWeekNumbers
                  scrollableYearDropdown
                />
                <InputCheck
                  Id="deleteOverlappingSeasons"
                  Name="deleteOverlappingSeasons"
                  Label="Poista olemassa olevat kaudet, jotka jäävät uusien päivämäärien väliin kokonaisuudessaan"
                  HandleChange={e =>
                    setDeleteOverlappingSeasons(e.target.checked)
                  }
                  Checked={deleteOverlappingSeasons}
                  IsRequired={false}
                />
                <Button
                  variant="primary"
                  className="mt-16 mb-16 w-100"
                  onClick={handleModifyDatesForSeasonRates}
                  disabled={isCopying}
                >
                  {isCopying
                    ? 'Muutetaan hintakausien päivämääriä...'
                    : 'Muuta hintakauden aikarajat'}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="my-48">
          <div
            className="border bg-white rounded shadow-lg mx-auto p-24 p-xl-32"
            style={{ maxWidth: 640 }}
          >
            <AccommodationSelector
              Subtitle="Päivämäärien muutokset tietylle kohteelle"
              Title="Valitse kohde"
              onAccommodationSelected={handleAccommodationSelected}
            />
            {selectedAccommodationId && (
              <>
                <p className="text-gray-600 mt-16 mb-16">
                  Hintakauden nykyinen alkamispäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedSeasonStartDate(date)}
                  className="form-control"
                  selected={selectedSeasonStartDate}
                  showWeekNumbers
                  showYearDropdown
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Hintakauden nykyinen päättymispäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedSeasonEndDate(date)}
                  className="form-control mb-16"
                  selected={selectedSeasonEndDate}
                  minDate={selectedSeasonStartDate ?? null}
                  disabled={!selectedSeasonStartDate}
                  showYearDropdown
                  showWeekNumbers
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Aseta hintakauden uusi alkamispäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedNewSeasonStartDate(date)}
                  className="form-control"
                  selected={selectedNewSeasonStartDate}
                  showWeekNumbers
                  showYearDropdown
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Aseta hintakauden uusi päättymispäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedNewSeasonEndDate(date)}
                  className="form-control mb-16"
                  selected={selectedNewSeasonEndDate}
                  minDate={selectedNewSeasonStartDate ?? null}
                  disabled={!selectedNewSeasonStartDate}
                  showYearDropdown
                  showWeekNumbers
                  scrollableYearDropdown
                />
                <InputCheck
                  Id="deleteOverlappingSeasons"
                  Name="deleteOverlappingSeasons"
                  Label="Poista olemassa olevat kaudet, jotka jäävät uusien päivämäärien väliin kokonaisuudessaan"
                  HandleChange={e =>
                    setDeleteOverlappingSeasons(e.target.checked)
                  }
                  Checked={deleteOverlappingSeasons}
                  IsRequired={false}
                />
                <Button
                  variant="primary"
                  className="mt-16 mb-16 w-100"
                  onClick={handleModifyDatesForSeasonRates}
                  disabled={isCopying}
                >
                  {isCopying
                    ? 'Muutetaan hintakausien päivämääriä...'
                    : 'Muuta hintakauden aikarajat'}
                </Button>
              </>
            )}
          </div>
        </div>

        {(copyMessages.length > 0 || updateSummary) && (
          <Card className="bg-white border shadow-none">
            <h6 className="text-uppercase text-muted m-2">
              Päivämäärämuutokset
            </h6>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Kohteen numero</th>
                  <th>Kohteen nimi</th>
                  <th>Kausia päivitetty</th>
                </tr>
              </thead>
              <tbody>
                {copyMessages.map(copyMessage => (
                  <tr
                    key={`${copyMessage.accommodationNumber}`}
                    className={
                      Number(copyMessage.seasonRatesChangedCount) === 0
                        ? 'table-warning'
                        : ''
                    }
                  >
                    <td>{copyMessage.accommodationNumber}</td>
                    <td>{copyMessage.accommodationName}</td>
                    <td>{copyMessage.seasonRatesChangedCount}</td>
                  </tr>
                ))}
                {updateSummary && (
                  <tr>
                    <td colSpan={2}>
                      <strong>Yhteensä</strong>
                    </td>
                    <td>
                      <strong>
                        Kohteita muutettu:{' '}
                        {updateSummary.accommodationsModifiedCount} — Kohteita
                        ei muutettu:{' '}
                        {updateSummary.accommodationsNotModifiedCount}
                      </strong>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card>
        )}

        {seasonRateBatchUpdateDetails && (
          <Accordion>
            {seasonRateBatchUpdateDetails.map((detail, index) => {
              const isRevertOperation =
                detail.operationType ===
                SEASON_RATE_BATCH_OPERATION_TYPE.DateChangeRevert;
              return (
                <Accordion.Item
                  eventKey={index.toString()}
                  key={detail.id ?? index}
                  className={isRevertOperation ? 'bg-light' : ''}
                >
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-start w-100 me-3">
                      <div>
                        <h6
                          className={`text-uppercase m-2 ${
                            isRevertOperation
                              ? 'text-secondary fst-italic'
                              : 'text-muted'
                          }`}
                        >
                          {isRevertOperation && '↶ '}
                          {
                            SEASON_RATE_BATCH_OPERATION_TYPE_DESCRIPTIONS[
                              detail.operationType
                            ]
                          }{' '}
                          -{' '}
                          {format(
                            new Date(detail.operationDate),
                            'dd.MM.yyyy HH:mm:ss',
                          )}
                        </h6>
                        {detail.requestParametersDescription && (
                          <div className="text-muted small ms-2 mb-1">
                            {detail.requestParametersDescription}
                          </div>
                        )}
                      </div>
                      {detail.isRevertible && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            setRevertModalState({
                              show: true,
                              batchId: detail.id,
                              operationType: detail.operationType,
                            });
                          }}
                        >
                          Palauta
                        </Button>
                      )}
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Card className="bg-white border shadow-none p-24 p-xl-32">
                      <strong>Valitut parametrit:</strong>{' '}
                      {detail.requestParametersDescription}
                      {detail.operationType ===
                        SEASON_RATE_BATCH_OPERATION_TYPE.DateChange && (
                        <p className="mt-2 mb-0">
                          <strong>Kohteita muutettu:</strong>{' '}
                          {detail.accommodationsCopiedCount ?? 0}
                          {' — '}
                          <strong>Kohteita ei muutettu:</strong>{' '}
                          {detail.accommodationsNotCopiedCount ?? 0}
                        </p>
                      )}
                      {detail.updateDetails &&
                        detail.updateDetails.length > 0 && (
                          <Table striped bordered hover>
                            <thead>
                              <tr>
                                <th>Kohteen numero</th>
                                <th>Kohteen nimi</th>
                                <th>Kausia muutettu</th>
                              </tr>
                            </thead>
                            <tbody>
                              {detail.updateDetails.map(updateDetail => (
                                <tr
                                  key={updateDetail.accommodationNumber}
                                  className={
                                    updateDetail.seasonRatesChangedCount === 0
                                      ? 'table-warning'
                                      : ''
                                  }
                                >
                                  <td>{updateDetail.accommodationNumber}</td>
                                  <td>{updateDetail.accommodationName}</td>
                                  <td>
                                    {updateDetail.seasonRatesChangedCount}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                    </Card>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}

        {revertModalState.show &&
          revertModalState.batchId &&
          revertModalState.operationType && (
            <RevertConfirmation
              ShowModal={revertModalState.show}
              BatchUpdateDetailId={revertModalState.batchId}
              OperationType={revertModalState.operationType}
              HideModal={() => setRevertModalState({ show: false })}
              OnReverted={() => {
                dispatch(
                  fetchSeasonRateBatchUpdateDetails(
                    SEASON_RATE_BATCH_OPERATION_TYPE.DateChange,
                  ),
                );
              }}
            />
          )}
      </Container>
    </>
  );
};

export default ModifySeasonRatesTool;
